import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import {
    ColDef,
    GridApi,
    GridOptions,
    GridReadyEvent,
} from 'ag-grid-community';
import { filter, Subject, takeUntil, tap } from 'rxjs';
import { RouteConstants } from '../../../../core/constants/routes';
import {
    InvoiceNet30TransitionablePaymentMethods,
    PaymentMethod,
    PaymentMethodLabelMapping,
} from '../../../../core/enums/commerce/payment/payment-method';
import { formatToDate } from '../../../../core/functions/date-helpers';
import { nameOf } from '../../../../core/functions/string-helpers';
import { IGridComponent } from '../../../../core/models/grid/grid-component';
import { GridHelper } from '../../../../core/models/grid/grid-helper';
import { CurrencyFormattingService } from '../../../../core/services/currency-formatting/currency-formatting.service';
import { OrderIdCellRendererComponent } from '../../../../shared/components/grid/cell-renderers/order-fulfillment/order-id-cell-renderer/order-id-cell-renderer.component';
import { GridToolbarComponent } from '../../../../shared/components/grid/grid-toolbar/grid-toolbar.component';
import { GridToolbarOptions } from '../../../../shared/components/grid/grid-toolbar/models/grid-toolbar-options';
import { UserStateService } from '../../../user/shared/services/user-state/user-state.service';
import { PaymentService } from '../shared/services/payment/payment.service';
import { CustomerPaymentDashboardGridData } from './models/customer-payment-dashboard-grid-data';
import { CustomerPaymentDashboardGridResponse } from './models/customer-payment-dashboard-grid-response';

@Component({
    selector: 'app-customer-payment-dashboard',
    templateUrl: './customer-payment-dashboard.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [AgGridAngular, GridToolbarComponent],
})
export class CustomerPaymentDashboardComponent
    implements IGridComponent, OnDestroy
{
    private destroy$ = new Subject<void>();

    constructor(
        private readonly paymentService: PaymentService,
        private readonly userStateService: UserStateService,
        private readonly currencyFormattingService: CurrencyFormattingService,
    ) {}

    context: unknown;
    rowData: CustomerPaymentDashboardGridData[] = [];
    gridApi: GridApi | undefined;
    columnDefs: ColDef[] = [];

    gridToolbarOptions: GridToolbarOptions = {
        ...GridHelper.defaultToolbarOptions,
        exportCsvOptions: {
            fileName: () => GridHelper.getExportFileName('Payments'),
        },
    };

    gridOptions: GridOptions = {
        ...GridHelper.defaultGridOptions,
    };

    onGridReady(params: GridReadyEvent<unknown>) {
        this.gridApi = params.api;

        this.loadGridData();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadGridData() {
        const authenticatedUser = this.userStateService.getUser();
        if (!authenticatedUser) {
            throw new Error('User is not authenticated');
        }

        this.paymentService
            .getAllForCustomer(authenticatedUser.id)
            .pipe(
                takeUntil(this.destroy$),
                tap((items) => {
                    this.rowData = this.transformRowData(items);
                    this.columnDefs = this.getColumnDefs();
                }),
                filter(() => !!this.gridApi),
            )
            .subscribe(() => {
                this.gridApi!.setGridOption('rowData', this.rowData);
                this.gridApi!.setGridOption('columnDefs', this.columnDefs);
            });
    }

    private readonly dateBetweenFilterParams = {
        ...GridHelper.defaultDateFilterParams,
        filterOptions: ['inRange'],
    };

    private transformRowData(
        items: CustomerPaymentDashboardGridResponse[],
    ): CustomerPaymentDashboardGridData[] {
        return items.map((item) => ({
            orderId: item.orderId,
            orderDate: formatToDate(item.orderDate),
            poNumber: item.poNumber ?? '',
            checkNumber: item.checkNumber ?? '',
            paymentDate: formatToDate(item.paymentDate),
            amount: item.amount,
            isInvoiceNet30: item.isInvoiceNet30,
            paymentMethod: item.paymentMethod,
        }));
    }

    private getColumnDefs(): ColDef[] {
        return [
            {
                field: nameOf<CustomerPaymentDashboardGridData>('orderId'),
                headerName: 'Order ID',
                cellRenderer: OrderIdCellRendererComponent,
                cellRendererParams: {
                    returnRoute: RouteConstants.Payment.getFullRoute('INDEX'),
                },
                minWidth: 130,
                maxWidth: 145,
            },
            {
                field: nameOf<CustomerPaymentDashboardGridData>('orderDate'),
                headerName: 'Order Date',
                ...GridHelper.defaultDateColDef,
                filterParams: this.dateBetweenFilterParams,
                minWidth: 180,
                maxWidth: 210,
            },
            {
                field: nameOf<CustomerPaymentDashboardGridData>('poNumber'),
                headerName: 'PO Number',
                minWidth: 130,
                maxWidth: 170,
            },
            {
                field: nameOf<CustomerPaymentDashboardGridData>('checkNumber'),
                headerName: 'Check Number',
                minWidth: 195,
                maxWidth: 215,
            },
            {
                field: nameOf<CustomerPaymentDashboardGridData>('paymentDate'),
                headerName: 'Payment Date',
                ...GridHelper.defaultDateColDef,
                filterParams: this.dateBetweenFilterParams,
                minWidth: 180,
                maxWidth: 210,
                sort: 'desc',
            },
            {
                headerName: 'Payment',
                field: nameOf<CustomerPaymentDashboardGridData>('amount'),
                valueFormatter: (params) => {
                    return this.currencyFormattingService.formatToUSD(
                        params.value as number,
                    );
                },
                minWidth: 130,
                maxWidth: 160,
                filter: false,
            },
            {
                field: nameOf<CustomerPaymentDashboardGridData>(
                    'paymentMethod',
                ),
                headerName: 'Payment Method',
                filter: false,
                sortable: false,
                minWidth: 180,
                flex: 1,
                valueGetter: (params) => {
                    const paymentMethod = params.data
                        ?.paymentMethod as PaymentMethod;

                    if (
                        params.data?.isInvoiceNet30 &&
                        InvoiceNet30TransitionablePaymentMethods.includes(
                            paymentMethod,
                        )
                    ) {
                        return `${PaymentMethodLabelMapping[PaymentMethod.InvoiceNet30]} - ${PaymentMethodLabelMapping[paymentMethod]}`;
                    }

                    return (
                        PaymentMethodLabelMapping[paymentMethod] ||
                        params.data?.paymentMethod
                    );
                },
            },
        ];
    }
}
